import type { MockDatabase, RoleCode } from "@/types/domain";
import type { MockRequest } from "@/mocks/router";
import { replaceStore, snapshotStore, store } from "@/mocks/store";
import {
  clearMockSession,
  getMockSession,
  setMockSession,
} from "@/mocks/session-state";
import { hashPassword } from "@/lib/session-crypto";
import { err, ok, type MockResponse } from "./_utils";

export { clearMockSession, getMockSession } from "@/mocks/session-state";

/** Demo accounts aligned with backend seed (`admin` / `admin123`). */
const MOCK_USERS: Record<string, { password: string; passwordMd5: string; role: RoleCode }> = {
  admin: { password: "admin123", passwordMd5: hashPassword("admin123"), role: "sdk_admin" },
  operator1: { password: "admin123", passwordMd5: hashPassword("admin123"), role: "operator" },
  developer1: { password: "admin123", passwordMd5: hashPassword("admin123"), role: "developer" },
};

function parseCookie(cookieHeader: string, name: string): string | null {
  if (!cookieHeader) return null;
  const parts = cookieHeader.split(/;\s*/);
  for (const part of parts) {
    const eq = part.indexOf("=");
    if (eq <= 0) continue;
    if (part.slice(0, eq).trim() === name) {
      return decodeURIComponent(part.slice(eq + 1).trim());
    }
  }
  return null;
}

function restoreSessionFromCookies(req: MockRequest) {
  if (getMockSession()) return;
  const cookie = req.headers?.cookie || "";
  const userId = parseCookie(cookie, "session_user_id");
  const value = parseCookie(cookie, "session_value");
  if (userId && value && MOCK_USERS[userId]) {
    setMockSession({ userId, value });
    store.SESSION.currentRole = MOCK_USERS[userId].role;
  }
}

function handleLogin(body: unknown): MockResponse {
  const payload = body as { user_id?: string; user_pwd?: string };
  const userId = (payload?.user_id || "").trim();
  const userPwd = payload?.user_pwd || "";
  if (!userId || !userPwd) {
    return err(400, "VALIDATION", "user_id and user_pwd are required");
  }
  const account = MOCK_USERS[userId];
  // Accept MD5 hex (client_nodejs / real API) or plaintext (legacy tests).
  const okPwd = !!account && (userPwd === account.passwordMd5 || userPwd === account.password);
  if (!okPwd) {
    return err(401, "INVALID_CREDENTIALS", "invalid user_id or password");
  }

  store.SESSION.currentRole = account.role;
  const value = `mock_sess_${Date.now().toString(36)}`;
  setMockSession({ userId, value });
  const role = store.ROLES[account.role];
  return ok({
    data: {
      currentRole: account.role,
      current_role: account.role,
      role,
      permissions: store.PERMISSIONS[account.role],
      session_user_id: userId,
      session_value: value,
    },
  });
}

export function handleSession(req: MockRequest): MockResponse | null {
  const method = req.method.toUpperCase();
  const path = req.path.replace(/\/+$/, "") || "/";
  const { body, query } = req;

  restoreSessionFromCookies(req);

  if (method === "GET" && path === "/api/bootstrap") return ok({ data: snapshotStore() });
  if (method === "PUT" && path === "/api/bootstrap") {
    replaceStore(body as MockDatabase);
    return ok({ data: snapshotStore() });
  }

  // Login — POST /api/session/create (client_nodejs) and legacy POST /api/session
  if (method === "POST" && (path === "/api/session/create" || path === "/api/session")) {
    return handleLogin(body);
  }

  if (method === "GET" && path === "/api/session/verify") {
    const session = getMockSession();
    if (!session) return ok({ valid: false });
    return ok({ valid: true, user_id: session.userId });
  }

  if (method === "GET" && path === "/api/session") {
    const session = getMockSession();
    const userId = query.get("user_id") || session?.userId;
    if (userId && MOCK_USERS[userId] && session?.userId === userId) {
      const roleCode = store.SESSION.currentRole;
      return ok({
        data: {
          currentRole: roleCode,
          role: store.ROLES[roleCode],
          permissions: store.PERMISSIONS[roleCode],
        },
      });
    }
    // Backward-compatible hydrate for tests without login
    if (!userId && !session) {
      const role = store.SESSION.currentRole;
      return ok({ data: { currentRole: role, role: store.ROLES[role], permissions: store.PERMISSIONS[role] } });
    }
    if (!session) return err(401, "INVALID_CREDENTIALS", "session not found or expired");
    const role = store.SESSION.currentRole;
    return ok({ data: { currentRole: role, role: store.ROLES[role], permissions: store.PERMISSIONS[role] } });
  }

  if (method === "POST" && path === "/api/session/role") {
    const payload = body as { role?: RoleCode; user_id?: string };
    const role = payload?.role;
    if (!role || !store.ROLES[role]) return err(400, "INVALID_ROLE", "未知角色");
    store.SESSION.currentRole = role;
    return ok({ data: { currentRole: role } });
  }

  if (method === "POST" && path === "/api/session/logout") {
    clearMockSession();
    return ok({ data: { ok: true } });
  }

  if (method === "GET" && path === "/api/me") return ok({ data: store.ROLES[store.SESSION.currentRole] });

  return null;
}
